import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import panzoom from 'panzoom';
import React, { useEffect, useRef, useState } from 'react';



// Small modal component with basic information and close button
const SmallModal = ({ details, onExpand, onClose, containerRef, isDarkMode }) => (

    <Box
        sx={{
            position: 'absolute',
            bottom: 30,
            height: 150,
            left: '50%',
            transform: 'translate(-50%)',
            minWidth: 200,
            border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
            borderRadius: 2,
            boxShadow: 3,
            paddingTop: '20px',
            paddingX: '20px',
            zIndex: 10,
            overflow: 'auto', alignContent: 'center',
            maxWidth: containerRef.current?.clientWidth || '100%',
            maxHeight: containerRef.current?.clientHeight || '100%',
            backgroundColor: (theme) => theme.palette.background.default,
            color: (theme) => theme.palette.text.primary,
        }}
    >
        <IconButton
            sx={{ position: 'absolute', top: 5, right: 5, color: '#1876d2' }}
            onClick={onClose}
        >
            <CloseIcon />
        </IconButton>
        <Typography variant="h6">{details.name}</Typography>
        <Typography variant="body2">{details.biome}</Typography>
        <Typography variant="body2">Known Locations: {details.scene_locations.length}</Typography>
        <Button
            variant="contained"
            size="small"
            onClick={onExpand}
            sx={{ margin: '20px auto', display: 'block' }}
        >
            More Info
        </Button>
    </Box>
);

// Full-screen modal but constrained within the parent container
const FullScreenModal = ({ details, onClose, containerRef, isDarkMode }) => (
    <Box
        sx={{
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translate(-50%)',
            minWidth: 200,
            width: '80%',
            height: '80%',
            border: isDarkMode ? '1px solid #ffffff' : '1px solid #000000',
            borderRadius: 2,
            boxShadow: 5,
            padding: 3,
            zIndex: 10,
            overflow: 'auto',
            maxWidth: containerRef.current?.clientWidth || '100%',
            maxHeight: containerRef.current?.clientHeight || '100%',
            backgroundColor: (theme) => theme.palette.background.default,
            color: (theme) => theme.palette.text.primary,
        }}
    >
        <Typography variant="h6">{details.name}</Typography>
        <IconButton
            sx={{ position: 'absolute', top: 5, right: 5, color: '#1876d2' }}
            onClick={onClose}
        >
            <CloseIcon />
        </IconButton>
        <Typography variant="body1" sx={{ marginTop: '10px' }}>{details.description}</Typography>
        <Typography variant="body1" sx={{ marginY: '10px' }}>Known Locations:</Typography>
        {details.scene_locations.length > 0 ? (
            details.scene_locations.map((scene, index) => (
                <Accordion key={index} sx={{ marginBottom: '10px', backgroundColor: isDarkMode ? '#111111' : '#eeeeee' }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${index}-content`}
                        id={`panel${index}-header`}
                    >
                        <Typography><strong>{scene.name}</strong></Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>{scene.setting}</Typography>
                    </AccordionDetails>
                </Accordion>
            ))
        ) : (
            <Typography variant="body2" sx={{ marginTop: '10px' }}>None</Typography>
        )}

    </Box>
);

const Map = ({ tabHeight, token, game_id, BASE_URL, checkpointId }) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const panZoomRef = useRef(null);
    const [playerPosition, setPlayerPosition] = useState({ x: 0, y: 0 });
    const [mapCells, setMapCells] = useState([]);
    const [selectedCell, setSelectedCell] = useState(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Refs for handling interactions
    const startPosRef = useRef(null);
    const isTouchDevice = useRef(false);
    const touchCount = useRef(0);
    const clickTimeoutRef = useRef(null);

    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    useEffect(() => {


        const fetchMapData = async () => {

            try {
                const mapEndpoint = checkpointId
                    ? `${BASE_URL}/v1/world-map/${game_id}/${checkpointId}`
                    : `${BASE_URL}/v1/world-map/${game_id}`;

                const response = await fetch(mapEndpoint, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data = await response.json();
                setPlayerPosition({ x: data.player_position_x, y: data.player_position_y });
                setMapCells(data.cells);
                setLoading(false);

            } catch (error) {
                setError(error);
            }

        };
        fetchMapData();

        // Detect touch device
        if ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0) {
            isTouchDevice.current = true;
        }

        const touchHandler = () => {
            isTouchDevice.current = true;
            window.removeEventListener('touchstart', touchHandler);
        };
        window.addEventListener('touchstart', touchHandler);

        return () => {
            window.removeEventListener('touchstart', touchHandler);
        };
    }, [BASE_URL, checkpointId, game_id, token]);

    useEffect(() => {
        if (!loading && svgRef.current) {  // Initialize panzoom only if not loading and svgRef is set
            const panZoomInstance = panzoom(svgRef.current, {
                maxZoom: 4,
                minZoom: 1,
                bounds: true,
            });

            panZoomRef.current = panZoomInstance;

            // Clean up on component unmount
            return () => {
                panZoomInstance.dispose();
            };
        }
    }, [loading]);

    const handlePointerDown = (row, col, e) => {
        if (isTouchDevice.current) {
            touchCount.current += 1;
            if (touchCount.current === 1) {
                startPosRef.current = { x: e.clientX, y: e.clientY, row, col };
            }
        } else {
            // For non-touch devices, set a timeout to differentiate between click and drag
            startPosRef.current = { x: e.clientX, y: e.clientY, row, col };
            clickTimeoutRef.current = setTimeout(() => {
                handleCellSelection(row, col);
            }, 200); // Adjust this delay as needed
        }
    };

    const handlePointerMove = (e) => {
        if (!startPosRef.current) return;

        const moveThreshold = 5; // pixels
        const dx = Math.abs(e.clientX - startPosRef.current.x);
        const dy = Math.abs(e.clientY - startPosRef.current.y);

        if (dx > moveThreshold || dy > moveThreshold) {
            // Clear the click timeout and reset startPosRef if we've moved beyond the threshold
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
                clickTimeoutRef.current = null;
            }
            startPosRef.current = null;
        }
    };

    const handlePointerUp = (e) => {
        if (isTouchDevice.current) {
            touchCount.current -= 1;
            if (touchCount.current === 0 && startPosRef.current) {
                const { row, col } = startPosRef.current;
                handleCellSelection(row, col);
            }
            if (touchCount.current < 0) touchCount.current = 0;
        } else {
            // For non-touch devices, if the timeout is still active, it's a click
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
                clickTimeoutRef.current = null;
                if (startPosRef.current) {
                    const { row, col } = startPosRef.current;
                    handleCellSelection(row, col);
                }
            }
        }
        startPosRef.current = null;
    };

    const handleCellSelection = (x, y) => {
        setIsFullScreen(false);
        const cell = mapCells.find(cell => cell.x === x && cell.y === y);
        if (cell) {
            setSelectedCell(cell);
            if (panZoomRef.current) {
                panZoomRef.current.pause();
            }
        }
    };

    const handleModalClose = () => {
        setSelectedCell(null);
        if (panZoomRef.current) {
            panZoomRef.current.resume();
        }
    };

    const handleExpandModal = () => {
        setIsFullScreen(true);
    };


    // Define border cell coordinates
    const borderCoordinates = [
        ...Array.from({ length: 10 }, (_, i) => ({ x: i, y: 0 })), // Top border (0,0 to 0,9)
        ...Array.from({ length: 10 }, (_, i) => ({ x: 0, y: i })), // Left border (0,0 to 9,0)
        ...Array.from({ length: 10 }, (_, i) => ({ x: i, y: 9 })), // Bottom border (9,0 to 9,9)
        ...Array.from({ length: 10 }, (_, i) => ({ x: 9, y: i })), // Right border (0,9 to 9,9)
    ];

    if (error) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: `calc(100vh - ${tabHeight})`
            }}>
                <Typography color="error">Error: {error.message}</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: `calc(100vh - ${tabHeight})`
            }}>
                <img src="/loading.gif"
                    alt="loading.gif"
                    style={{ height: '100px', width: 'auto' }} />
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{ height: `calc(100vh - ${tabHeight})`, borderRadius: '8px', position: 'relative', display: 'flex', alignItems: 'center', overflow: 'hidden' }}
            ref={containerRef}
        >
            <svg
                ref={svgRef}
                viewBox="0 0 1000 1000"
                style={{
                    touchAction: 'none',
                    overflow: 'hidden',
                    borderRadius: '8px',
                }}
            >
                {mapCells.map((cell) => (
                    <g key={`${cell.y}-${cell.x}`}>
                        <image
                            href={`${BASE_URL}/v1/image/${game_id}/map-${cell.y + 1}-${cell.x + 1}.png?type=map&token=${token}`}
                            x={(cell.x + 1) * 100}
                            y={(cell.y + 1) * 100}
                            width="100"
                            height="100"
                            onPointerDown={(e) => handlePointerDown(cell.x, cell.y, e)}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerUp}
                            style={{ cursor: 'pointer' }}
                        />

                        {/* Render the border cells */}
                        {borderCoordinates.map((borderCell, index) => (
                            <image
                                key={`border-${index}`}
                                href={`${BASE_URL}/v1/image/${game_id}/map-${borderCell.y}-${borderCell.x}.png?type=map&token=${token}&a=c`}
                                x={borderCell.x * 100}
                                y={borderCell.y * 100}
                                width="100"
                                height="100"
                                style={{ pointerEvents: 'none' }}
                            />
                        ))}

                        <rect
                            x={(cell.x + 1) * 100 + 2}
                            y={(cell.y + 1) * 100 + 2}
                            width="96"
                            height="96"
                            fill="none"
                            stroke={selectedCell?.x === cell.x && selectedCell?.y === cell.y ? '#1876d2' : 'none'}
                            strokeWidth={selectedCell?.x === cell.x && selectedCell?.y === cell.y ? 4 : 0}
                        />
                    </g>
                ))}

                <image
                    href="/x.png"
                    x={(playerPosition.x + 1) * 100 + 25}  // Adjust for the image's dimensions
                    y={(playerPosition.y + 1) * 100 + 25}  // Adjust for the image's dimensions
                    width="50"                       // Set width to your desired size
                    height="50"                      // Set height to your desired size
                    style={{ pointerEvents: 'none' }}
                />

            </svg>

            {selectedCell && (
                <>
                    {isFullScreen ? (
                        <FullScreenModal
                            details={selectedCell}
                            onClose={handleModalClose}
                            containerRef={containerRef}
                            isDarkMode={isDarkMode}
                        />
                    ) : (
                        <SmallModal
                            details={selectedCell}
                            onExpand={handleExpandModal}
                            onClose={handleModalClose}
                            containerRef={containerRef}
                            isDarkMode={isDarkMode}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default Map;